import { graphqlClient } from '../graphqlClient';

const getRestBaseUrl = () => {
  if (import.meta.env.VITE_REST_API_URL) return import.meta.env.VITE_REST_API_URL;
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL.replace(/\/graphql\/?$/, '');
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('luxkgate.com') || hostname.includes('vercel.app')) return '';
  }
  return 'http://localhost:3000';
};

const getInfrastructureStatus = async () => {
  const query = `
    query {
      infrastructureStatus {
        uptimePercentage
        status
        latencyMs
      }
    }
  `;

  const response = await graphqlClient.query(query);
  return response.infrastructureStatus;
};

const getSystemStatus = async () => {
  const query = `
    query {
      systemStatus {
        infrastructure {
          uptimePercentage
          status
          latencyMs
        }
        apiGateway {
          uptimePercentage
          status
          latencyMs
          cpu
          memory
          disk
          networkIn
          networkOut
        }
        database {
          uptimePercentage
          status
          latencyMs
          cpu
          memory
          disk
          networkIn
          networkOut
        }
      }
    }
  `;

  const response = await graphqlClient.query(query);
  return response.systemStatus;
};

const getApiGatewayLogs = async (limit = 100) => {
  const query = `
    query GetApiGatewayLogs($limit: Int) {
      apiGatewayLogs(limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { limit });
  return response.apiGatewayLogs;
};

const getDatabaseLogs = async (limit = 100) => {
  const query = `
    query GetDatabaseLogs($limit: Int) {
      databaseLogs(limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { limit });
  return response.databaseLogs;
};

const getInfrastructureLogs = async (limit = 100) => {
  const query = `
    query GetInfrastructureLogs($limit: Int) {
      infrastructureLogs(limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { limit });
  return response.infrastructureLogs;
};

const getApiGatewayLogsFiltered = async (startDate, endDate, limit = 100) => {
  const query = `
    query GetApiGatewayLogsFiltered($startDate: String, $endDate: String, $limit: Int) {
      apiGatewayLogsFiltered(startDate: $startDate, endDate: $endDate, limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { startDate, endDate, limit });
  return response.apiGatewayLogsFiltered;
};

const getDatabaseLogsFiltered = async (startDate, endDate, limit = 100) => {
  const query = `
    query GetDatabaseLogsFiltered($startDate: String, $endDate: String, $limit: Int) {
      databaseLogsFiltered(startDate: $startDate, endDate: $endDate, limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { startDate, endDate, limit });
  return response.databaseLogsFiltered;
};

const getBankserverLogs = async (limit = 100) => {
  const query = `
    query GetBankserverLogs($limit: Int) {
      bankserverLogs(limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { limit });
  return response.bankserverLogs;
};

const getBankserverLogsFiltered = async (startDate, endDate, limit = 100) => {
  const query = `
    query GetBankserverLogsFiltered($startDate: String, $endDate: String, $limit: Int) {
      bankserverLogsFiltered(startDate: $startDate, endDate: $endDate, limit: $limit) {
        logs {
          id
          status
          latencyMs
          createdAt
        }
        serverStartTime
        databaseConnectionTime
        currentMemoryUsage
      }
    }
  `;

  const response = await graphqlClient.query(query, { startDate, endDate, limit });
  return response.bankserverLogsFiltered;
};

const getApiGatewayCpuPublic = async () => {
  const response = await fetch(`${getRestBaseUrl()}/api/public/api-gateway/cpu`);
  if (!response.ok) throw new Error('Failed to fetch CPU usage');
  return response.json();
};

const getBankserverStatusPublic = async () => {
  const response = await fetch(`${getRestBaseUrl()}/public/system/bankserver`);
  if (!response.ok) throw new Error('Failed to fetch bankserver status');
  return response.json();
};

const getBankserverHealth = async () => {
  try {
    // Try to fetch from configured bankserver URL first
    const bankserverUrl = import.meta.env.VITE_BANKSERVER_URL || 'http://localhost:5000';
    const response = await fetch(`${bankserverUrl}/status`);
    if (!response.ok) throw new Error('Failed to fetch bankserver health');
    const data = await response.json();
    
    // Transform the response to match expected format
    return {
      cpu: data.system?.cpu || 0,
      memory: data.system?.memory || 0,
      disk: data.system?.disk || 0,
      latency: data.system?.latencyMs || 0,
      uptime: data.system?.uptime || 0,
      networkIn:
        typeof data.system?.networkIn === 'number'
          ? data.system.networkIn
          : (typeof data.system?.network?.in === 'number' ? data.system.network.in : null),
      networkOut:
        typeof data.system?.networkOut === 'number'
          ? data.system.networkOut
          : (typeof data.system?.network?.out === 'number' ? data.system.network.out : null),
      status: 'healthy'
    };
  } catch (err) {
    console.error('Failed to fetch bankserver health:', err);
    // Fallback if direct request fails
    return {
      cpu: 0,
      memory: 0,
      disk: 0,
      latency: 0,
      uptime: 0,
      networkIn: null,
      networkOut: null,
      status: 'unknown'
    };
  }
};

/**
 * Fetch logs for a given server. Maps server name to the appropriate API.
 * @param {{ name: string }} server - Server object with name (e.g. "API Gateway", "Database Primary")
 * @param {string|null} startDate - Optional start date for filtered range
 * @param {string|null} endDate - Optional end date for filtered range
 * @returns {Promise<{ logs: Array, serverStartTime?: string, databaseConnectionTime?: string, currentMemoryUsage?: number }|{ error: string }>}
 */
const getLogs = async (server, startDate = null, endDate = null) => {
  const name = server?.name || '';
  const useFiltered = startDate || endDate;
  const limit = 100;

  try {
    if (name === 'API Gateway') {
      return useFiltered
        ? await getApiGatewayLogsFiltered(startDate, endDate, limit)
        : await getApiGatewayLogs(limit);
    }
    if (name === 'Database Primary' || name === 'Database Replica') {
      return useFiltered
        ? await getDatabaseLogsFiltered(startDate, endDate, limit)
        : await getDatabaseLogs(limit);
    }
    if (name === 'Payment Processor' || name === 'Bankserver') {
      const logsResponse = useFiltered
        ? await getBankserverLogsFiltered(startDate, endDate, limit)
        : await getBankserverLogs(limit);

      const health = await getBankserverHealth();
      return {
        ...logsResponse,
        currentMemoryUsage:
          logsResponse?.currentMemoryUsage ??
          (typeof health?.memory === 'number' ? health.memory : null),
      };
    }
    return { logs: [], serverStartTime: null, databaseConnectionTime: null, currentMemoryUsage: null };
  } catch (err) {
    return { error: err?.message || 'Failed to fetch logs', logs: [] };
  }
};

export { getInfrastructureStatus, getSystemStatus, getApiGatewayLogs, getDatabaseLogs, getInfrastructureLogs, getApiGatewayLogsFiltered, getDatabaseLogsFiltered, getBankserverLogs, getBankserverLogsFiltered, getApiGatewayCpuPublic, getBankserverStatusPublic, getBankserverHealth, getLogs };
export default getInfrastructureStatus;